import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isActiveWorkflow } from "@/lib/activeWorkflowGuard";

const INACTIVITY_KEY = "last_activity_timestamp";
const INACTIVITY_TIMEOUT_MS = 48 * 60 * 60 * 1000; // 48 hours in milliseconds

export const useInactivityLogout = () => {
  const navigate = useNavigate();

  // Update activity timestamp
  const updateActivity = useCallback(() => {
    localStorage.setItem(INACTIVITY_KEY, Date.now().toString());
  }, []);

  // Check if user has been inactive for too long
  const checkInactivity = useCallback(async () => {
    const lastActivity = localStorage.getItem(INACTIVITY_KEY);
    
    if (lastActivity) {
      const lastActivityTime = parseInt(lastActivity, 10);
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime;
      
      if (timeSinceActivity >= INACTIVITY_TIMEOUT_MS) {
        // WORKFLOW GUARD: Never log out during an active appraisal
        if (isActiveWorkflow()) {
          console.log("[Inactivity] Would log out but active workflow detected — skipping");
          updateActivity(); // Reset the timer
          return false;
        }

        console.log("[Inactivity] User inactive for 48+ hours, logging out");
        
        // Clear the activity timestamp
        localStorage.removeItem(INACTIVITY_KEY);
        
        // Sign out the user
        await supabase.auth.signOut();
        
        toast.info("You've been logged out due to inactivity", {
          description: "Please log in again to continue.",
          duration: 6000,
        });
        
        navigate("/auth");
        return true; // User was logged out
      }
    } else {
      // No activity recorded yet, set it now
      updateActivity();
    }
    
    return false; // User is still active
  }, [navigate, updateActivity]);

  useEffect(() => {
    // Check inactivity on mount
    checkInactivity();

    // Update activity on user interactions
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also update on visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkInactivity();
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Check inactivity periodically (every hour)
    const intervalId = setInterval(() => {
      checkInactivity();
    }, 60 * 60 * 1000); // Check every hour

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [checkInactivity, updateActivity]);

  return { updateActivity, checkInactivity };
};

// Utility function to force logout all sessions (call this from admin)
export const forceLogoutCurrentSession = async () => {
  localStorage.removeItem(INACTIVITY_KEY);
  await supabase.auth.signOut();
};
